import { useEffect, useRef, useState } from 'react'
import { ArrowUpDown, ChevronDown, ChevronUp, GripVertical } from 'lucide-react'
import Button from '../../components/Button'
import IconButton from '../../components/IconButton'
import Modal from '../../components/Modal'
import { strings as S } from './strings'
import './ProgramOrderModal.css'

const O = S.ordering

/**
 * ترتيب البرامج التدريبية — `_ProgramManagement_Ordering_Modal.cshtml`.
 *
 * The order the learner's panel lists programmes in. The button that opens this has been
 * on the page since it was built and had no handler; `SaveProgramsOrder` has been there
 * the whole time.
 *
 * DRAGGED OR MOVED WITH BUTTONS — both, and the pair is the point. Dragging is how the
 * legacy does it and how anyone expects to arrange a short list, so the row carries a
 * handle and reorders live as it passes its neighbours. But a drag-and-drop list on its
 * own is unusable by keyboard and awkward on a touch screen, so up and down stay: they
 * name the item they move, and they are the path that works without a pointer. No
 * library — the HTML5 drag events do this on their own.
 *
 * The order is only sent when SAVE is pressed. Reordering in place and posting each move
 * would write a half-finished order every time a row passed another.
 */
export default function ProgramOrderModal({ open, programs, onClose, onSubmit, saving }) {
  const [items, setItems] = useState([])

  // Reopened: start from the stored order again, not from an abandoned edit.
  useEffect(() => {
    if (open) setItems(programs ?? [])
  }, [open, programs])

  const move = (index, delta) => {
    const target = index + delta
    if (target < 0 || target >= items.length) return

    setItems((previous) => {
      const next = [...previous]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  /* ---- Dragging ----
     The row being carried, and the one it is currently over. Held in a ref as well as in
     state: the dragover handler fires many times a second and needs the current index
     without waiting for a render. */
  const dragFrom = useRef(null)
  const [dragging, setDragging] = useState(null)

  const onDragStart = (index) => (event) => {
    dragFrom.current = index
    setDragging(index)
    /* Firefox starts no drag at all without data on the transfer. */
    event.dataTransfer.effectAllowed = 'move'
    try {
      event.dataTransfer.setData('text/plain', String(index))
    } catch {
      /* Some browsers refuse this outside a real user drag; the ref is what we read. */
    }
  }

  /* The list reorders as the pointer passes a row rather than on drop, so what you see
     while dragging is what you get — and the moved row keeps up with the cursor. */
  const onDragEnter = (index) => () => {
    const from = dragFrom.current
    if (from === null || from === index) return

    setItems((previous) => {
      const next = [...previous]
      const [carried] = next.splice(from, 1)
      next.splice(index, 0, carried)
      return next
    })
    dragFrom.current = index
    setDragging(index)
  }

  const endDrag = () => {
    dragFrom.current = null
    setDragging(null)
  }

  return (
    <Modal
      open={open}
      size="sm"
      title={O.title}
      icon={ArrowUpDown}
      hint={O.hint}
      onClose={onClose}
      footer={
        <Button disabled={saving} onClick={() => onSubmit(items.map((item) => item.id))}>
          {saving ? O.saving : O.save}
        </Button>
      }
    >
      <ol className="progorder">
        {items.map((item, index) => (
          <li
            key={item.id}
            className={`progorder__row${dragging === index ? ' is-dragging' : ''}`}
            draggable
            onDragStart={onDragStart(index)}
            onDragEnter={onDragEnter(index)}
            /* Without preventDefault the browser refuses the drop and animates the row
               snapping back to where it started. */
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault()
              endDrag()
            }}
            onDragEnd={endDrag}
          >
            {/* Says the row can be carried. The whole row is draggable, so this is a
                label for the affordance rather than the only place that starts a drag. */}
            <span className="progorder__grip" aria-hidden="true">
              <GripVertical size={16} strokeWidth={1.75} />
            </span>
            {/* The position, which is the whole point of the list — without it the
                reader has to count rows to know what they are changing. */}
            <span className="progorder__rank">{index + 1}</span>
            <bdi className="progorder__name">{item.name}</bdi>
            <span className="progorder__moves">
              <IconButton
                icon={ChevronUp}
                size="sm"
                label={`${O.moveUp}: ${item.name}`}
                disabled={index === 0}
                onClick={() => move(index, -1)}
              />
              <IconButton
                icon={ChevronDown}
                size="sm"
                label={`${O.moveDown}: ${item.name}`}
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
              />
            </span>
          </li>
        ))}
      </ol>
    </Modal>
  )
}
